import { Spacer } from "@/components/Spacer";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

export default function Section06({ isMobile }: { isMobile: boolean }) {
  return (
    <div className="w-full flex desktop:flex-row flex-col items-center desktop:justify-center bg-[#ffffff] desktop:py-[160px] desktop:px-[32px] py-[80px] px-[20px]">
      <motion.div
        id="section-06-container"
        className="desktop:w-[960px] w-[342px] flex desktop:flex-row flex-col desktop:gap-[60px]"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 100,
          mass: 1,
          delay: 0.1,
        }}
      >
        <div className="desktop:w-full desktop:max-w-[379px] flex flex-col desktop:items-start items-center justify-start">
          <div
            className="font-pre-bold text-[#5364F2] leading-[1.2em] desktop:text-[20px] text-[14px] desktop:text-start text-center"
            style={{ letterSpacing: "-0.02em" }}
          >
            어떤 증상인지 알았다면?
          </div>
          <Spacer height={12} />
          <div className="whitespace-pre-line font-pre-bold text-[#17191C] leading-[140%] desktop:text-[32px] text-[20px] desktop:text-start text-center">
            증상에 맞는 병원을{"\n"}간편하게 예약해요
          </div>
          <Spacer height={16} />
          <div
            className="whitespace-pre-line font-pre-medium text-[#2E323A] leading-[140%] desktop:text-[18px] text-[16px] desktop:text-start text-center"
            style={{ letterSpacing: "-0.02em" }}
          >
            증상에 맞는 진료과 추천부터 내 주변{"\n"}병원과 약국까지 한번에
            확인할 수 있어요
          </div>

          <Spacer height={isMobile ? 16 : 54} />
          <div
            id="section-06-carousel"
            className="desktop:w-[379.5px] w-[342px] desktop:h-[110px] h-[86px] overflow-hidden"
          >
            <Carousel isMobile={isMobile} />
          </div>
          <Spacer height={isMobile ? 32 : 60} />
        </div>

        <div className="w-full flex justify-center items-start">
          <img
            src="assets/images/section-06-02.png"
            alt="section-06-02"
            className="desktop:w-[521px] desktop:h-[350px] w-[342px] h-[230px]"
          />
        </div>
      </motion.div>
    </div>
  );
}

interface CarouselProps {
  isMobile: boolean;
}

type ItemProps = {
  image: string;
  title: string;
};

const Carousel = ({ isMobile }: CarouselProps) => {
  const urlPrefix = "assets/images/subjects/";
  const images: ItemProps[] = isMobile
    ? [
        { image: `${urlPrefix}FamilyMedicine-mobile.png`, title: "가정의학과" },
        { image: `${urlPrefix}InternalMedicine-mobile.png`, title: "내과" },
        {
          image: `${urlPrefix}AnesthesiologyandPainMedicine-mobile.png`,
          title: "마취통증의학과",
        },
        { image: `${urlPrefix}Urology-mobile.png`, title: "비뇨기과" },
        {
          image: `${urlPrefix}ObstetricsandGynecology-mobile.png`,
          title: "산부인과",
        },
        { image: `${urlPrefix}PlasticSurgery-mobile.png`, title: "성형외과" },
        { image: `${urlPrefix}Pediatrics-mobile.png`, title: "소아청소년과" },
        { image: `${urlPrefix}Neurology-mobile.png`, title: "신경과" },
        { image: `${urlPrefix}Neurosurgery-mobile.png`, title: "신경외과" },
        { image: `${urlPrefix}Ophthalmology-mobile.png`, title: "안과" },
        { image: `${urlPrefix}Radiology-mobile.png`, title: "영상의학과" },
        { image: `${urlPrefix}GeneralSurgery-mobile.png`, title: "외과" },
        {
          image: `${urlPrefix}Otorhinolaryngology-mobile.png`,
          title: "이비인후과",
        },
        {
          image: `${urlPrefix}RehabilitationMedicine-mobile.png`,
          title: "재활의학과",
        },
        { image: `${urlPrefix}Psychiatry-mobile.png`, title: "정신건강의학과" },
        {
          image: `${urlPrefix}OrthopedicSurgery-mobile.png`,
          title: "정형외과",
        },
        { image: `${urlPrefix}Dentistry-mobile.png`, title: "치과" },
        { image: `${urlPrefix}Dermatology-mobile.png`, title: "피부과" },
        { image: `${urlPrefix}ThoracicSurgery-mobile.png`, title: "흉부외과" },
      ]
    : [
        {
          image: `${urlPrefix}FamilyMedicine-desktop.png`,
          title: "가정의학과",
        },
        { image: `${urlPrefix}InternalMedicine-desktop.png`, title: "내과" },
        {
          image: `${urlPrefix}AnesthesiologyandPainMedicine-desktop.png`,
          title: "마취통증의학과",
        },
        { image: `${urlPrefix}Urology-desktop.png`, title: "비뇨기과" },
        {
          image: `${urlPrefix}ObstetricsandGynecology-desktop.png`,
          title: "산부인과",
        },
        { image: `${urlPrefix}PlasticSurgery-desktop.png`, title: "성형외과" },
        { image: `${urlPrefix}Pediatrics-desktop.png`, title: "소아청소년과" },
        { image: `${urlPrefix}Neurology-desktop.png`, title: "신경과" },
        { image: `${urlPrefix}Neurosurgery-desktop.png`, title: "신경외과" },
        { image: `${urlPrefix}Ophthalmology-desktop.png`, title: "안과" },
        { image: `${urlPrefix}Radiology-desktop.png`, title: "영상의학과" },
        { image: `${urlPrefix}GeneralSurgery-desktop.png`, title: "외과" },
        {
          image: `${urlPrefix}Otorhinolaryngology-desktop.png`,
          title: "이비인후과",
        },
        {
          image: `${urlPrefix}RehabilitationMedicine-desktop.png`,
          title: "재활의학과",
        },
        {
          image: `${urlPrefix}Psychiatry-desktop.png`,
          title: "정신건강의학과",
        },
        {
          image: `${urlPrefix}OrthopedicSurgery-desktop.png`,
          title: "정형외과",
        },
        { image: `${urlPrefix}Dentistry-desktop.png`, title: "치과" },
        { image: `${urlPrefix}Dermatology-desktop.png`, title: "피부과" },
        { image: `${urlPrefix}ThoracicSurgery-desktop.png`, title: "흉부외과" },
      ];

  // 두 배의 이미지를 생성하여 무한 스크롤 효과 구현
  const duplicatedImages = [...images, ...images];

  const getImageSize = (image: string) => {
    // 이미지 경로에서 파일명만 추출 (경로와 확장자 제거)
    const fileName = image.split("/").pop()?.split("-")[0] || "";

    // 특별한 크기가 필요한 이미지들의 맵 정의
    const specialSizes = {
      AnesthesiologyandPainMedicine: isMobile
        ? { width: 92, height: 86 }
        : { width: 117, height: 110 },
      Pediatrics: isMobile
        ? { width: 79, height: 86 }
        : { width: 101, height: 110 },
      Psychiatry: isMobile
        ? { width: 92, height: 86 }
        : { width: 117, height: 110 },
    };

    // 특별한 크기가 있으면 해당 크기 반환, 없으면 기본 크기 반환
    return (
      specialSizes[fileName as keyof typeof specialSizes] ||
      (isMobile ? { width: 74, height: 86 } : { width: 97, height: 110 })
    );
  };

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* 클리핑 뷰포트 - 높이 제한 */}
      <div className="relative w-full h-full overflow-hidden">
        <div className="absolute top-0 left-0 desktop:h-[326px] h-[386px] w-[15%] z-10 bg-gradient-to-r from-[#FFFFFF] to-transparent pointer-events-none"></div>
        <div className="flex items-center h-full overflow-hidden">
          <motion.div
            className="flex gap-[2px] h-full"
            animate={{
              x: [
                0,
                -(images.length * (isMobile ? 120 : 140) + images.length * 5),
              ],
            }}
            transition={{
              x: {
                repeat: Infinity,
                repeatType: "loop",
                duration: images.length * 7,
                ease: "linear",
              },
            }}
          >
            {duplicatedImages.map((image, index) => (
              <motion.div
                key={index}
                className={`h-full flex-shrink-0 overflow-hidden flex items-center justify-center`}
                transition={{ duration: 0.3 }}
              >
                <img
                  src={image.image}
                  alt={image.title}
                  style={{
                    width: `${getImageSize(image.image).width}px`,
                    height: `${getImageSize(image.image).height}px`,
                    objectFit: "contain",
                  }}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
        <div className="absolute top-0 right-0 h-full w-[15%] z-10 bg-gradient-to-l from-[#FFFFFF] to-transparent pointer-events-none"></div>
      </div>
    </div>
  );
};
